(() => {
  'use strict';
  if (window.PiccoAppMotion) return;

  const cards = [
    '.picco-ref-search-card', '.product-card', '[data-product-card]',
    '.horizontal-product', '.article-card', '.collection-card', '.pc-card',
    '.picco-rf__card', '.picco-iwp__card', '.picco-rvf__card',
    '.picco-favourites__item', '.picco-rec-card', '.pcu-card', '.jdgm-rev'
  ].join(',');
  const panels = [
    '#MainContent > .shopify-section', 'main > .shopify-section',
    '.picco-ref-mobile-nav__panel', '.picco-ref-search__view',
    '[data-menu-navigation]', '[data-menu-search-view]',
    '.picco-support-app-panel', '[data-picco-support-panel]',
    '.picco-ref-mega', '.drawer__content', '.drawer__scrollable',
    '.picco-ref-drawer__content', '.picco-rf__drawer-body',
    '.pc-quick-add__body', '.pcu-dialog__body',
    '[data-track123-section="progress-bar"]',
    '[data-track123-section="tracking-details"]',
    '[data-track123-section="product-recommendation"]',
    '[role="alert"]', '[data-picco-animate]'
  ].join(',');
  const selector = cards + ',' + panels;
  // These drawers and the native Notify form already animate themselves.
  // Keep their fixed positioning and measured form height under their own control.
  const nativeDrawers = '.picco-rf__drawer, .pc-quick-add__shell, .picco-ref-quickshop, [data-iwp-quick-shell], .pcu-dialog';
  const nativeNotify = '[data-picco-omnisend-bis-slot], .picco-bis-native-host, .picco-omnisend-shell, #PiccoBisNativeParking, #PiccoOmnisendExactVariantPool, [id^="omnisend-form-"]';
  const states = new WeakMap();
  const watched = new Set();
  const roots = new Set();
  const stoppedCarousels = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let observer;

  function syncBackgroundCarousels() {
    const drawerOpen = Array.from(document.querySelectorAll(nativeDrawers)).some(drawer =>
      !drawer.hidden && drawer.getAttribute('aria-hidden') !== 'true' &&
      (drawer.classList.contains('is-open') || drawer.hasAttribute('open'))
    );
    if (drawerOpen) {
      document.querySelectorAll('.swiper-initialized').forEach(host => {
        if (host.closest(nativeDrawers)) return;
        const swiper = host.swiper;
        if (swiper?.destroyed || !swiper?.autoplay?.running || typeof swiper.autoplay.stop !== 'function') return;
        stoppedCarousels.set(swiper, host);
        swiper.autoplay.stop();
      });
    } else {
      stoppedCarousels.forEach((host, swiper) => {
        if (host.isConnected && !swiper.destroyed) swiper.autoplay?.start?.();
      });
      stoppedCarousels.clear();
    }
  }

  function excluded(element) {
    return !!element.closest('[data-picco-cart-resolved], .picco-cart-pending, [data-picco-cart-item], .picco-cart-media, #CartDrawer [data-picco-recs], [data-picco-motion-skip]') ||
      !!element.closest(nativeDrawers + ',' + nativeNotify) ||
      element.matches('#MainContent > .shopify-section, main > .shopify-section') && !!element.querySelector(nativeDrawers + ',' + nativeNotify) ||
      !!element.closest('#CartDrawer .drawer__content, #CartDrawer .drawer__scrollable') && !element.matches(cards);
  }

  function visible(element) {
    if (!element.isConnected || excluded(element) || !element.getClientRects().length) return false;
    if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const parentStyle = getComputedStyle(parent);
      if (parentStyle.visibility === 'hidden' || parentStyle.display === 'none') return false;
    }
    return true;
  }

  function inView(element) {
    const box = element.getBoundingClientRect();
    return box.bottom > 0 && box.right > 0 && box.top < window.innerHeight && box.left < window.innerWidth;
  }

  function enter(element, delay = 0, force = false) {
    if (!visible(element) || !inView(element)) return;
    const state = states.get(element);
    if (!state || state.visible && !force) return;
    state.visible = true;
    if (reduced.matches || element.matches('motion-element, [data-animate]') || !element.animate) return;
    state.animation?.cancel();
    const moving = element.matches(cards);
    const frames = moving
      ? [{ opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }]
      : [{ opacity: 0 }, { opacity: 1 }];
    const animation = element.animate(frames, {
      duration: 340, delay: Math.min(delay, 150), easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards'
    });
    state.animation = animation;
    animation.finished.catch(() => {}).then(() => { if (state.animation === animation) state.animation = null; });
  }

  function watch(element, index = 0, force = false) {
    if (excluded(element)) return;
    if (!states.has(element)) {
      states.set(element, { visible: false, animation: null });
      watched.add(element);
      observer?.observe(element);
    }
    if (!visible(element)) {
      if (!element.matches(cards)) states.get(element).visible = false;
      return;
    }
    enter(element, index * 25, force);
  }

  function reveal(root, options = {}) {
    if (!(root instanceof Element) && root !== document) return;
    if (root instanceof Element && excluded(root)) return;
    if (root instanceof Element && (root.matches(selector) || options.force)) watch(root, 0, !!options.force);
    root.querySelectorAll(selector).forEach((element, index) => watch(element, index % 7, !!options.force));
    root.querySelectorAll('.picco-ref-search-card__image img').forEach(bindSearchImage);
  }

  const images = new WeakSet();
  function bindSearchImage(image) {
    if (images.has(image)) return;
    images.add(image);
    const host = image.closest('.picco-ref-search-card__image');
    if (!host) return;
    const finish = () => {
      host.classList.remove('picco-ui-image-loading');
      image.classList.add('picco-ui-image-ready');
    };
    image.addEventListener('load', finish);
    image.addEventListener('error', finish);
    if (image.complete) finish();
    else host.classList.add('picco-ui-image-loading');
  }

  function flush() {
    frame = 0;
    syncBackgroundCarousels();
    const pending = Array.from(roots);
    roots.clear();
    pending.forEach(root => { if (root.isConnected) reveal(root); });
    watched.forEach(element => {
      if (!element.isConnected) {
        observer?.unobserve(element);
        watched.delete(element);
      }
    });
  }

  function queue(root) {
    if (!(root instanceof Element) || root.matches('script, style, link, meta')) return;
    roots.add(root);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  function boot() {
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        entries.forEach((entry, index) => {
          const state = states.get(entry.target);
          if (!state) return;
          // Scrolling or a drawer resizing must not replay existing product cards.
          if (!entry.isIntersecting) {
            if (!entry.target.matches(cards)) state.visible = false;
          } else enter(entry.target, index * 25);
        });
      }, { threshold: 0.01 });
    }
    reveal(document);
    syncBackgroundCarousels();
    new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'childList') record.addedNodes.forEach(queue);
        else queue(record.target);
      });
    }).observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'aria-expanded', 'open', 'active', 'class', 'style']
    });
    document.addEventListener('toggle', event => {
      if (event.target instanceof HTMLDetailsElement && event.target.open) {
        Array.from(event.target.children).filter(child => child.tagName !== 'SUMMARY').forEach(child => {
          watch(child, 0, true);
          reveal(child);
        });
      }
    }, true);
    document.addEventListener('shopify:section:load', event => reveal(event.target));
    reduced.addEventListener?.('change', () => {
      if (reduced.matches) watched.forEach(element => states.get(element)?.animation?.cancel());
    });
  }

  window.PiccoAppMotion = { reveal };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
